import * as R from 'ramda';
import { put, call, select, takeLatest } from 'redux-saga/effects';
// components
import { closeModal } from '../../components/modal/actions';
import { closeLoader, openLoader } from '../../components/loader/actions';
import { transformSearchCriteriaBeforeReportPost } from '../../components/edit-report/helpers';
// features
import { makeSelectCurrentBranchGuid } from '../branch/selectors';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// report-common
import { getReportSagas } from '../../report-common';
// sagas
import { visitPageSaga } from '../../sagas';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// feature toll charges
import * as A from './actions';
import {
  makeSelectTruckGuid,
  makeSelectDriverGuid,
  makeSelectPagination,
  makeSelectUsedReport,
  makeSelectTotalCount,
  makeSelectTitleSortValues,
  makeSelectAvailableReports,
  makeSelectTableTitleFilters,
  makeSelectAdditionalSearchCriteria,
} from './selectors';
//////////////////////////////////////////////////

export function* handleGetItemListSaga({ payload }) {
  try {
    if (G.isTrue(G.getPropFromObject('openLoader', payload))) {
      yield put(openLoader({ showDimmer: true }));
    }

    yield put(A.setListLoading(true));

    const reportParams = yield select(makeSelectUsedReport());
    const availableReports = yield select(makeSelectAvailableReports());
    const additionalSearchCriteria = yield select(makeSelectAdditionalSearchCriteria());

    if (R.and(
      G.isNilOrEmpty(availableReports),
      G.isNilOrEmpty(G.getPropFromObject('fields', reportParams)),
    )) {
      yield put(A.setListLoading(false));

      return yield put(closeLoader());
    }

    const truckGuid = yield select(makeSelectTruckGuid());
    const driverGuid = yield select(makeSelectDriverGuid());
    const pagination = yield select(makeSelectPagination());
    const titleOrderFields = yield select(makeSelectTitleSortValues());
    const titleFilterParams = yield select(makeSelectTableTitleFilters());

    const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();

    if (G.isNilOrEmpty(reportParams.fields)) {
      const totalCount = yield select(makeSelectTotalCount());

      const data = {
        pagination,
        totalCount,
        results: [],
      };

      yield put(A.setListLoading(false));

      yield put(closeLoader());

      yield put(A.getItemListSuccess({ data }));
    }

    if (G.isTrue(payload)) yield put(openLoader());

    yield put(A.setListLoading(true));

    const orderFields = G.ifElse(
      G.isNotEmpty(titleOrderFields),
      R.values(titleOrderFields),
      G.getOrElse(reportParams, 'orderFields', []),
    );

    const searchCriteria = G.getSearchCriteriaWithTitleFilters(reportParams, titleFilterParams);

    const guids = R.pathOr(null, ['guids'], payload);

    const { limit, offset } = pagination;

    const systemFields = [
      { name: GC.FIELD_TRUCK_GUID, freezed: false, sequence: 100, reference: false },
      { name: GC.FIELD_DRIVER_GUID, freezed: false, sequence: 101, reference: false },
    ];

    const reqBody = {
      guids,
      limit,
      offset,
      orderFields,
      systemFields,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      fields: G.getOrElse(reportParams, 'fields', []),
      searchCriteria: transformSearchCriteriaBeforeReportPost(R.concat(searchCriteria, additionalSearchCriteria)),
    };

    let options = {
      data: reqBody,
    };

    if (G.isNotNilAndNotEmpty(truckGuid)) {
      const params = { truckGuid };

      options = R.assoc('params', params, options);
    }

    if (G.isNotNilAndNotEmpty(driverGuid)) {
      const params = { driverGuid };

      options = R.assoc('params', params, options);
    }

    const res = yield call(sendRequest, 'post', endpointsMap.tollChargesList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getItemListSuccess({ data, guids }));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetItemListSaga fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException(error, 'handleGetItemListSaga exception'));
  } finally {
    yield put(closeLoader());
    yield put(A.setListLoading(false));
  }
}

export function* updateDriverTollChargeRequest({ payload }) {
  try {
    yield put(openLoader());

    const { guid, driverGuid } = payload;

    const endpoint = endpointsMap.updateTollChargeDriver(guid);

    const options = {
      params: { driverGuid },
    };

    const res = yield call(sendRequest, 'put', endpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());
      yield put(A.updateTollChargeSuccess(data));

      yield call(G.showToastrMessage, 'success', 'toastr:success:201');
    } else {
      yield call(G.handleFailResponse, res, 'updateDriverTollChargeRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException(error, 'updateDriverTollChargeRequest exception'));
  }
}

export function* updateTruckTollChargeRequest({ payload }) {
  try {
    yield put(openLoader());

    const { guid, truckGuid } = payload;

    const endpoint = endpointsMap.updateTollChargeTruck(guid);

    const options = {
      params: { truckGuid },
    };

    const res = yield call(sendRequest, 'put', endpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());
      yield put(A.updateTollChargeSuccess(data));

      yield call(G.showToastrMessage, 'success', 'toastr:success:201');
    } else {
      yield call(G.handleFailResponse, res, 'updateTruckTollChargeRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException(error, 'updateTruckTollChargeRequest exception'));
  }
}

export function* assignDriverTruckRequest({ payload }) {
  try {
    yield put(openLoader());

    const options = { data: payload };

    const res = yield call(sendRequest, 'put', endpointsMap.tollChargeAssign, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());
      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest());

      yield call(G.showToastrMessage, 'success', 'toastr:success:201');
    } else {
      yield call(G.handleFailResponse, res, 'assignDriverTruckRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException(error, 'assignDriverTruckRequest exception'));
  }
}

export function* getDriverListRequest() {
  try {
    const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();

    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: currentBranchGuid,
      },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.listFleetDriversFullNameInTree, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.receivedDriverListSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'getDriverListRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException(error, 'getDriverListRequest exception'));
  }
}

export function* getTruckListRequest() {
  try {
    const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();

    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: currentBranchGuid,
      },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.listFleetTrucks, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getTruckListSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'getTruckListRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException(error, 'getTruckListRequest exception'));
  }
}

export function* handleExportReportDataSaga({ payload }) {
  try {
    yield put(openLoader());

    const truckGuid = yield select(makeSelectTruckGuid());
    const driverGuid = yield select(makeSelectDriverGuid());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    let searchCriteria = transformSearchCriteriaBeforeReportPost(payload.searchCriteria);

    if (G.isOneNotNilOrNotEmpty([truckGuid, driverGuid])) {
      const filter = {
        collection: false,
        dataType: 'string',
        operation: 'equal',
        stringValue: R.or(truckGuid, driverGuid),
        propertyName: G.ifElse(G.isNilOrEmpty(truckGuid), GC.FIELD_DRIVER_GUID, GC.FIELD_TRUCK_GUID),
      };

      searchCriteria = R.append(filter, searchCriteria);
    }

    const reqBody = {
      searchCriteria,
      fields: payload.fields,
      orderFields: payload.orderFields,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      [GC.FIELD_REPORT_NAME]: G.getPropFromObject(GC.FIELD_NAME, payload),
    };

    const options = {
      data: reqBody,
      resType: 'arraybuffer',
      params: { format: payload.fileType },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.tollChargeExportReport, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'info', 'messages:downloading-file');
    } else {
      yield call(G.handleFailResponse, res, 'handleExportReportDataSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleExportReportDataSaga exception');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  }
}

const createUpdateReportSuccessCallback = (data) => G.getReportSortedBySeqFreez(data);

const {
  handleAvailableReportsRequest,
  handleCreateReportRequestSaga,
  handleUpdateReportRequestSaga,
  handleChangeDefaultReportSaga,
} = getReportSagas(
  GC.TOLL_CHARGE_REPORT,
  A,
  handleGetItemListSaga,
  { createUpdateReportSuccessCallback },
);

export function* visitTollChargesOnFleetPage({ payload }) {
  while (true) { // eslint-disable-line
    yield put(openLoader());
    yield put(A.setInitialState());

    const { truckGuid, driverGuid } = payload;

    yield put(A.setReportPending());
    yield put(A.setTruckGuid(truckGuid));
    yield put(A.setDriverGuid(driverGuid));

    if (G.isNilOrEmpty(truckGuid)) yield call(getTruckListRequest);
    if (G.isNilOrEmpty(driverGuid)) yield call(getDriverListRequest);

    yield call(handleAvailableReportsRequest, { payload: { [GC.REPORT_TYPE]: GC.TOLL_CHARGE_REPORT } });
    yield call(handleGetItemListSaga, { payload: { openLoader: true } });

    yield put(closeLoader());

    break;
  }
}

export function* visitTollChargesPageSaga({ payload }) {
  while (true) { // eslint-disable-line
    yield call(visitPageSaga, payload, GC.CHECK_VISIT_TOLL_CHARGES_PAGE);
    yield put(openLoader());

    yield put(A.setInitialState());
    yield put(A.setReportPending());
    yield put(A.setAdditionalSearchCriteria(R.path(['options', 'additionalSearchCriteria'], payload)));

    yield call(getTruckListRequest);
    yield call(getDriverListRequest);
    yield call(handleAvailableReportsRequest, { payload });
    yield call(handleGetItemListSaga, { payload: { openLoader: true } });

    yield put(closeLoader());

    break;
  }
}

function* tollChargesWatcherSaga() {
  yield takeLatest(A.getItemListRequest, handleGetItemListSaga);
  yield takeLatest(GC.VISIT_TOLL_CHARGES_PAGE, visitTollChargesPageSaga);
  yield takeLatest(A.createReportRequest, handleCreateReportRequestSaga);
  yield takeLatest(A.updateReportRequest, handleUpdateReportRequestSaga);
  yield takeLatest(A.exportReportDataRequest, handleExportReportDataSaga);
  yield takeLatest(A.assignDriverTruckRequest, assignDriverTruckRequest);
  yield takeLatest(A.visitTollChargesOnFleetPage, visitTollChargesOnFleetPage);
  yield takeLatest(A.changeDefaultReportRequest, handleChangeDefaultReportSaga);
  yield takeLatest(A.updateTruckTollChargeRequest, updateTruckTollChargeRequest);
  yield takeLatest(A.updateDriverTollChargeRequest, updateDriverTollChargeRequest);
}

export default tollChargesWatcherSaga;
